import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Sparkles, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import { infantilAPI } from '../services/api';

const API_URL = window.location.origin || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

export default function PlanningPage() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [gaps, setGaps] = useState(null);
  const [aiPlan, setAiPlan] = useState('');
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { infantilAPI.classes().then(setClasses); }, []);

  useEffect(() => {
    if (!selectedClass) return;
    setLoading(true);
    fetch(`${API_URL}/api/infantil360/planning/gaps?class_id=${selectedClass}&trimester=${encodeURIComponent('1º Trimestre')}`, { headers: headers() })
      .then(r => r.json()).then(d => { setGaps(d); setLoading(false); });
  }, [selectedClass]);

  const className = classes.find(c => c.id === selectedClass)?.name || '';

  const handleGenerate = async () => {
    if (!selectedClass) return;
    setGenerating(true);
    try {
      const obs = await infantilAPI.observations.list({ class_id: selectedClass, limit: 100 });
      const summary = (obs.items || []).map(o => `[${o.context}] ${o.title}`).join('\n') || 'Sem observações.';
      const gapStr = gaps?.field_gaps?.map(f => f.name).join(', ') || 'Nenhum gap identificado';
      const res = await fetch(`${API_URL}/api/infantil360/ai/plan`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ class_name: className, observations_summary: summary, gaps: gapStr })
      }).then(r => r.json());
      if (res.ok) { setAiPlan(res.plan); toast.success('Planejamento gerado!'); }
    } catch { toast.error('Erro'); }
    finally { setGenerating(false); }
  };

  return (
    <motion.div className="space-y-6" initial="hidden" animate="visible" variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
      data-testid="planning-page">
      <motion.div variants={fadeUp}>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Calendar className="h-5 w-5 text-[#3B82F6]" /> Planejamento Pedagógico</h1>
        <p className="text-sm text-slate-500 mt-1">Análise de gaps e sugestões de experiências com IA</p>
      </motion.div>

      <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <Label className="text-xs">Turma</Label>
            <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setAiPlan(''); }}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
              <option value="">Selecione...</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <Button onClick={handleGenerate} disabled={generating || !selectedClass} className="rounded-xl"
            style={{ background: 'linear-gradient(135deg, #3B82F6, #1d4ed8)' }}>
            <Sparkles className="h-4 w-4 mr-2" /> {generating ? 'Gerando...' : 'Gerar Sugestões IA'}
          </Button>
        </div>
      </motion.div>

      {/* Gap Analysis */}
      {gaps && !loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Campos BNCC</h3>
            <p className="text-xs text-slate-500 mb-3">{gaps.covered_bncc_fields}/{gaps.total_bncc_fields} cobertos</p>
            <div className="space-y-2">
              {gaps.field_gaps?.length > 0 ? gaps.field_gaps.map((f, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-red-50 border border-red-100">
                  <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                  <span className="text-xs text-red-700">{f.name} — sem observações</span>
                </div>
              )) : (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span className="text-xs text-emerald-700">Todos os campos cobertos!</span>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Dimensões</h3>
            <p className="text-xs text-slate-500 mb-3">{gaps.covered_dimensions}/{gaps.total_dimensions} cobertas</p>
            <div className="space-y-2">
              {gaps.dimension_gaps?.length > 0 ? gaps.dimension_gaps.map((d, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 border border-amber-100">
                  <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0" />
                  <span className="text-xs text-amber-700">{d.name} — sem observações</span>
                </div>
              )) : (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 border border-emerald-100">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span className="text-xs text-emerald-700">Todas as dimensões cobertas!</span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* AI Plan */}
      {aiPlan && (
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#3B82F6]" /> Sugestões de Planejamento (IA)</h3>
          <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{aiPlan}</div>
        </motion.div>
      )}
    </motion.div>
  );
}
